// Location: src/styles/layoutStyles.js
// Date: 20240929
// Author: R Matthews
// Purpose: Styles for layout components

import { makeStyles, tokens } from '@fluentui/react-components';

/**
 * Base styles for containers used in the layout.
 * 
 * The containerBaseStyles object provides a foundational set of styles for
 * flex containers used in the layout. It ensures that all child elements are
 * displayed in a row, wrapped as necessary, and evenly spaced.
 */
const containerBaseStyles = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: tokens.spacingM,
  justifyContent: 'space-evenly',
};

/**
 * useLayoutStyles
 * 
 * This hook provides styles for different layout containers and elements within
 * the application. The styles are organized to handle various screen sizes and
 * ensure that the layout is responsive.
 * 
 * - container: Basic container style that ensures full width and height, with no margin or padding.
 * - container1col: Single column layout. Each child takes the full width minus the gap.
 * - container2col: Two-column layout. Each child takes half the width minus the gap.
 * - container3col: Three-column layout. Each child takes one-third of the width minus the gap.
 * - container4col: Four-column layout. Each child takes one-fourth of the width minus the gap.
 * - mainContent: Style for the main content area, providing padding for spacing.
 * - cardGrid: Grid layout specifically for cards, with responsive adjustments based on screen size.
 * 
 * Usage:
 * 
 * To use these styles, import the useLayoutStyles hook and apply the classes
 * to your components. Example:
 * 
 * ```jsx
 * import React from 'react';
 * import { useLayoutStyles } from './styles/layoutStyles';
 * 
 * const MyComponent = () => {
 *   const styles = useLayoutStyles();
 * 
 *   return (
 *     <div className={styles.container}>
 *       <div className={styles.container2col}>
 *         <div>Column 1</div>
 *         <div>Column 2</div>
 *       </div>
 *       <div className={styles.mainContent}>
 *         Main content goes here
 *       </div>
 *     </div>
 *   );
 * };
 * 
 * export default MyComponent;
 * ```
 */
export const useLayoutStyles = makeStyles({
  container: {
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
  },
  container1col: {
    ...containerBaseStyles,
    '& > *': {
      flex: '1 1 calc(100% - 16px)',
    },
  },
  container2col: {
    ...containerBaseStyles,
    '& > *': {
      flex: '1 1 calc(50% - 16px)',
    },
  },
  container3col: {
    ...containerBaseStyles,
    '& > *': {
      flex: '1 1 calc(33.33% - 16px)',
    },
  },
  container4col: {
    ...containerBaseStyles,
    '& > *': {
      flex: '1 1 calc(25% - 16px)',
    },
  },
  mainContent: {
    flex: 1,
    padding: tokens.spacingM,
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(250px, 1fr))',
    gap: tokens.spacingM,
    padding: tokens.spacingM,
    margin: tokens.spacingM,
    height: '100%', // Ensure it stretches to the parent's height
    justifyContent: 'center', // Center the grid items horizontally
    alignContent: 'center', // Center the grid items vertically
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
    '@media (min-width: 769px) and (max-width: 1024px)': {
      gridTemplateColumns: 'repeat(2, minmax(250px, 1fr))',
    },
    '@media (orientation: landscape) and (max-width: 768px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    },
  },
});